/* istanbul ignore file */
import {ITPAParams} from '@wix/tpa-style-webpack-plugin/dist/runtime/types';
import type {CustomCssVarsFnParams, ShouldRenderVarsFnParams} from '@wix/wixstores-client-storefront-common';
import {CSSProperties} from 'react';
import {
  GalleryNavigationPosition,
  GalleryNavigationType,
  ImageRatio,
  LayoutId,
  MediaNavArrowsDisplayMode,
} from '../../../constants';

const getAspectRatio = (ratioEnum: number): number | null => {
  switch (ratioEnum) {
    case ImageRatio.THREE_BY_TWO:
      return 3 / 2;
    case ImageRatio.FOUR_BY_THREE:
      return 4 / 3;
    case ImageRatio.ONE_BY_ONE:
      return 1;
    case ImageRatio.THREE_BY_FOUR:
      return 3 / 4;
    case ImageRatio.TWO_BY_THREE:
      return 2 / 3;
    case ImageRatio.AUTO:
      return null;
  }
};

const getNavigationType = (strings: Record<string, any>): GalleryNavigationType => {
  return strings?.productPage_galleryNavigationType?.value as GalleryNavigationType;
};

const getNavigationPosition = (strings: Record<string, any>): GalleryNavigationPosition => {
  return strings?.productPage_galleryNavigationPosition?.value as GalleryNavigationPosition;
};

const getNavigationMarginOld = (navigationPosition: GalleryNavigationPosition, gap: number) => {
  switch (navigationPosition) {
    case GalleryNavigationPosition.LEFT:
      return `0 ${gap}px 0 0`;
    case GalleryNavigationPosition.RIGHT:
      return `0 0 0 ${gap}px`;
    case GalleryNavigationPosition.BOTTOM:
    default:
      return `${gap}px 0 0 0`;
  }
};

const getNavigationMargin = (styleParams: ITPAParams) => {
  const navigationType = getNavigationType(styleParams.strings);
  const navigationPosition = getNavigationPosition(styleParams.strings);
  const gap = styleParams.numbers.productPage_mainMediaNavigationGap;

  if (navigationType === GalleryNavigationType.DOTS) {
    return `${gap}px 0 0 0`;
  }

  switch (navigationPosition) {
    case GalleryNavigationPosition.LEFT:
      return `0 ${gap}px 0 0`;
    case GalleryNavigationPosition.RIGHT:
      return `0 0 0 ${gap}px`;
    case GalleryNavigationPosition.BOTTOM:
      return `${gap}px 0 0 0`;
    default:
      return '0';
  }
};

const getMediaFlexDirectionOld = (
  navigationPosition: GalleryNavigationPosition,
  isRTL: boolean
): CSSProperties['flexDirection'] => {
  switch (navigationPosition) {
    case GalleryNavigationPosition.LEFT:
      // eslint-disable-next-line sonarjs/no-duplicate-string
      return isRTL ? 'row' : 'row-reverse';
    case GalleryNavigationPosition.RIGHT:
      return isRTL ? 'row-reverse' : 'row';
    case GalleryNavigationPosition.BOTTOM:
    default:
      return 'column';
  }
};

const getMediaFlexDirection = (styleParams: ITPAParams, isRTL: boolean): CSSProperties['flexDirection'] => {
  const navigationType = getNavigationType(styleParams.strings);
  if (navigationType === GalleryNavigationType.DOTS) {
    return 'column';
  }

  const navigationPosition = getNavigationPosition(styleParams.strings);
  switch (navigationPosition) {
    case GalleryNavigationPosition.LEFT:
      return isRTL ? 'row' : 'row-reverse';
    case GalleryNavigationPosition.RIGHT:
      return isRTL ? 'row-reverse' : 'row';
    case GalleryNavigationPosition.BOTTOM:
    default:
      return 'column';
  }
};

const getThumbnailHeight = (styleParams: ITPAParams) => {
  const {productPage_thumbnailsSize} = styleParams.numbers;
  const thumbnailAspectRatio = getAspectRatio(styleParams.numbers.productPage_thumbnailImageRatio);
  if (!thumbnailAspectRatio) {
    return 'initial';
  }

  return `${productPage_thumbnailsSize / thumbnailAspectRatio}px`;
};

const getNavigationSize = (styleParams: ITPAParams) => {
  const {productPage_thumbnailsSize} = styleParams.numbers;

  const navigationType = getNavigationType(styleParams.strings);
  if (navigationType !== GalleryNavigationType.THUMBNAILS) {
    return '24px';
  }

  const navigationPosition = getNavigationPosition(styleParams.strings);
  const isVertical = navigationPosition !== GalleryNavigationPosition.BOTTOM;
  if (isVertical) {
    return `${productPage_thumbnailsSize}px`;
  }
  return getThumbnailHeight(styleParams);
};

const getGalleryCompositeHeight = (layoutId: number): CSSProperties['height'] => {
  return layoutId === LayoutId.Stunning ? 'auto' : '100%';
};

export const getProductGalleryLayoutCustomCssVars = ({styleParams, isRTL, isMobile}: CustomCssVarsFnParams) => {
  const {productPage_thumbnailsSize, productPage_layoutId, productPage_mainMediaNavigationGap} = styleParams.numbers;
  const thumbnailAspectRatio = getAspectRatio(styleParams.numbers.productPage_thumbnailImageRatio);
  const mainMediaAspectRatio = getAspectRatio(styleParams.numbers.productPage_galleryImageRatio);

  const thumbnailsContainerSpacing = 15;
  const thumbnailArea = Number(productPage_thumbnailsSize) + thumbnailsContainerSpacing;
  const thumbnailsHeight = Number(productPage_thumbnailsSize) / thumbnailAspectRatio;

  const navigationPosition = getNavigationPosition(styleParams.strings);
  const isVertical = navigationPosition !== GalleryNavigationPosition.BOTTOM;

  const arrowDisplayMode = styleParams.numbers?.productPage_mainMediaNavArrowsDisplayMode as MediaNavArrowsDisplayMode;
  const mainMediaNavArrowsIsAlwaysShown = isMobile || arrowDisplayMode !== MediaNavArrowsDisplayMode.HOVER ? '1' : '0';

  return {
    navigationMarginOld: getNavigationMarginOld(navigationPosition, productPage_mainMediaNavigationGap),
    navigationMargin: getNavigationMargin(styleParams),
    navigationSize: getNavigationSize(styleParams),
    thumbnailSize: isVertical ? getThumbnailHeight(styleParams) : `${productPage_thumbnailsSize}px`,
    mediaFlexDirectionOld: getMediaFlexDirectionOld(navigationPosition, isRTL),
    mediaFlexDirection: getMediaFlexDirection(styleParams, isRTL),
    productPage_thumbnailsHeight: `${thumbnailsHeight}px`,
    productPage_thumbnailArea: `${thumbnailArea}px`,

    mainMediaAspectRatio: mainMediaAspectRatio ?? 'initial',
    thumbnailsAspectRatio: thumbnailAspectRatio ?? 'initial',
    galleryCompositeHeight: getGalleryCompositeHeight(productPage_layoutId),
    mainMediaNavArrowsIsAlwaysShown,
  };
};

export const getProductGalleryLayoutShouldRenderVars = ({strings}: ShouldRenderVarsFnParams) => {
  const navigationPosition = getNavigationPosition(strings);
  const navigationType = getNavigationType(strings);
  const isHorizontal = navigationPosition === GalleryNavigationPosition.BOTTOM;

  return {
    shouldRenderDotsNavigation: navigationType === GalleryNavigationType.DOTS,
    shouldRenderHorizontalThumbnails: navigationType === GalleryNavigationType.THUMBNAILS && isHorizontal,
    shouldRenderVerticalThumbnails: navigationType === GalleryNavigationType.THUMBNAILS && !isHorizontal,
  };
};
